import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isJsonObject, Json, JsonObject } from '@se/cp.common';
import {
    CpHttpService,
    EstensaWaitingService,
    IPatientHistory,
    ModalDialogNotificationService,
    ModalWindowSize,
} from '@se/platform';
import { DataBinding, Logger, SeConstraintNotifierService } from '@se/se.platform.base';
import { isEmpty, isUndefined } from 'lodash-es';
import moment, { Moment } from 'moment';
import { firstValueFrom } from 'rxjs';
import { CpImportControl, ICpEntity, PropertyType } from '../../cp-configuration/view-model';
import { CpDialogService } from '../../cp-dialog-service';
import { CpModelService } from '../../cp-model.service';
import { CpReportService } from '../../cp-report.service';
import { AbstractControlComponent } from '../abstract-control';
import { CpImportGridComponent } from './import-grid/import-grid.component';
import { CpImportService, ICpPatientHistory } from './import.service';
@Component({
    selector: 'cp-import',
    templateUrl: './import.component.html',
    styleUrls: ['./import.component.scss'],
    standalone: false,
})
export class CpImportComponent extends AbstractControlComponent {
    @Input() onlyIcon: boolean = false;

    maxReturnedExams!: number;

    control!: CpImportControl;
    patientHistoryList: ICpPatientHistory[] = [];

    private patientHistoryRetrieved = false;

    constructor(
        private cpDialogService: CpDialogService,
        private httpService: CpHttpService,
        private importService: CpImportService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private modelService: CpModelService,
        private reportService: CpReportService,
        private translate: TranslateService,
        private waitingService: EstensaWaitingService,
        protected cdr: ChangeDetectorRef,
        protected constraintNotifierService: SeConstraintNotifierService,
        protected logger: Logger,
    ) {
        super(cdr, constraintNotifierService, logger);
    }

    // #region overrides
    protected onModelChange(_: unknown): boolean {
        return false;
    }

    // #endregion

    init(): void {
        this.maxReturnedExams = this.control.maxReturnedExams;

        if (!this.control.dataBindings || isEmpty(this.control.dataBindings))
            throw new Error('Binding missing for import component.');

        if (isUndefined(this.maxReturnedExams) || this.maxReturnedExams < 0)
            throw new Error('Invalid maxReturnedExam property for import component');
    }

    async retrievePatientHistory(): Promise<void> {
        if (this.patientHistoryRetrieved) {
            return;
        }

        const report = this.modelService.getEntity(new DataBinding('Report'));
        const patient = report?.getEntity(new DataBinding('Patient'));

        const idPatient = patient?.getPropertyValue('Id') as string;

        let isPreview = this.modelService.getEntity(new DataBinding())?.getPropertyValue('IsPreview') ?? false;

        if (isPreview || !idPatient) {
            //se il patient Id è undefined allora sono standalone

            this.patientHistoryList = [];
            return;
        }

        const examDate = this.reportService.examDate ?? moment();
        const bindings = this.control.dataBindings.map((db) => db.dataBindingSource.toStringSimple());

        try {
            let idModelTypeRootList: string[] | undefined;
            if (this.control.onlyOnSameDataset) {
                const idModelTypeRootListReport = report?.getPropertyValue('IdModelTypeRootList') as string[];
                if (isEmpty(idModelTypeRootListReport)) {
                    throw new Error('Import control: idModelTypeList is expected not to be empty');
                }

                idModelTypeRootList = idModelTypeRootListReport;
            }

            const idCustomPageRoot = this.control.onlyOnSameLayout
                ? report?.getPropertyValue('IdCustomPageRoot')?.toString()
                : undefined;

            const val = await firstValueFrom(
                this.httpService.retrievePatientHistory$({
                    maxReturnedExams: this.maxReturnedExams,
                    idPatient,
                    thresholdDate: examDate.toJSON(),
                    bindings,
                    idModelTypeRootList,
                    idCustomPageRoot,
                }),
            );

            return this.patientHistoryCallback(val, bindings);
        } catch (error) {
            this.patientHistoryErrorCallback(error, idPatient, examDate);
        }
    }

    // #region Callbacks

    private async patientHistoryCallback(val: IPatientHistory[], bindings: string[]): Promise<void> {
        this.patientHistoryRetrieved = true;
        this.patientHistoryList = val;

        for (let i = 0; i < bindings.length; i++) {
            const binding = bindings[i];

            this.patientHistoryList.forEach((ph: ICpPatientHistory) => {
                if (this.isVnextExam(ph)) ph[binding] = 'Loading... ';
                else {
                    if (!!ph.requestedValues) ph[binding] = ph.requestedValues[i];
                }
            });
        }

        const getDataCallings: any[] = [];

        this.patientHistoryList.forEach((ph: ICpPatientHistory) => {
            if (this.isVnextExam(ph)) {
                getDataCallings.push(
                    firstValueFrom(
                        this.httpService.getData(ph.idExaminations, undefined, true),
                        // .pipe(timeout(200))
                    )
                        .then((json: Json) => this.getDataCallback(json, ph.idExaminations, bindings))
                        .catch((error) => this.getDataErrorCallback(error, ph.idExaminations, bindings)),
                );
            }
        });

        await Promise.all(getDataCallings);
    }

    private patientHistoryErrorCallback(error: any, idPatient: string, examDate: Moment) {
        this.patientHistoryRetrieved = true;

        if (this.logger) {
            this.logger.error(
                `Error during retrieving patient history with idPatient  ${idPatient} and examDate ${examDate}`,
            );
            this.logger.error(error);
        }
    }

    private getDataCallback(json: Json, idExaminations: string[], bindings: string[]) {
        // MB: Deprecated: Use value === undefined || value === null instead.
        if (json === null || json === undefined) return;

        if (!isJsonObject(json)) {
            throw new Error(`Expecting a json object from getData method - idExaminations = ${idExaminations}.`);
        }

        bindings.forEach((binding) => {
            const data = this.getBindingValue(binding, json);
            const patientHistory = this.findPatientHistory(idExaminations);

            if (!!patientHistory) patientHistory[binding] = data;
            else throw new Error(`Can't find patientHistory for idExaminations ${idExaminations}`);
        });
    }

    private getDataErrorCallback(error: any, idExaminations: string[], bindings: string[]) {
        bindings.forEach((binding) => {
            const patientHistory = this.findPatientHistory(idExaminations);

            if (!!patientHistory) {
                patientHistory[binding] = this.translate.instant('ImportNoData');
            }

            if (this.logger) {
                this.logger.error(`Error getting data idExaminations:${idExaminations}`);
                this.logger.error(error);
            }
        });
    }

    // #endregion

    async onClick() {
        //maxReturnedExams == 0 significa che non pesco dal sever,
        //ma recupero i dati dal referto corrente
        if (this.maxReturnedExams == 0) {
            this.importService.updateBindingTarget(this.control.dataBindings);
            this.edited.emit();
            return;
        }

        try {
            this.waitingService.isWaiting = true;

            await this.retrievePatientHistory();
        } finally {
            this.waitingService.isWaiting = false;
        }

        if (this.maxReturnedExams == 1) {
            if (this.patientHistoryList.length > 0) {
                const dataToImport = this.patientHistoryList[0];
                const validation = this.importService.validateData(dataToImport, this.control.dataBindings);

                if (validation.isEmpty) {
                    this.modalDialogNotificationService.info(
                        this.translate.instant('NoDataToImport'),
                        this.translate.instant('ImportData'),
                    );
                    return;
                }

                if (validation.onError) {
                    this.modalDialogNotificationService.error(this.translate.instant('ErrorLoadingPatientHistory'));
                    return;
                }

                this.importService.updateBindingTarget(this.control.dataBindings, dataToImport);
                this.edited.emit();
            } else
                this.modalDialogNotificationService.info(
                    this.translate.instant('NoDataToImport'),
                    this.translate.instant('ImportData'),
                );

            return;
        }

        this.openGrid();
    }

    // #region privates

    private findPatientHistory(idExaminations: string[]): ICpPatientHistory | undefined {
        return this.patientHistoryList.find((item) => item.idExaminations == idExaminations);
    }

    private getBindingValue(binding: string, json: JsonObject): PropertyType {
        const bindingPathInsideReport = new DataBinding(binding).next;
        const dataRoot: ICpEntity = this.modelService.makeEntity(json);
        return dataRoot.getValue(bindingPathInsideReport);
    }

    private isVnextExam(ph: ICpPatientHistory): boolean {
        if (!ph.idExaminations) {
            return false;
        }
        if (ph.idExaminations.length == 0) {
            return false;
        }
        return !ph.requestedValues || ph.requestedValues.every((rv) => !rv);
    }

    private openGrid() {
        const windowSize = this.control.dataBindings.length > 2 ? ModalWindowSize.fullScreen : ModalWindowSize.medium;

        this.cpDialogService.open(
            CpImportGridComponent,
            {
                patientHistoryList: this.patientHistoryList,
                dataBindings: this.control.dataBindings,
            },
            {
                windowSize,
                noPaddingInContent: true,
                header: this.translate.instant('ImportPreviousData'),
            },
        );
    }

    // #endregion
}
